import { supabase, ensureValidSession } from './client';
import { updatePropertyRentalStatus } from './properties';

/**
 * Get all leases for a property
 * @param {string} propertyId - Property ID
 * @returns {Promise<Array>} Array of leases
 */
export async function getLeases(propertyId) {
  try {
    const { data, error } = await supabase
      .from('leases')
      .select('*')
      .eq('property_id', propertyId)
      .order('start_date', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching leases:', error);
    throw error;
  }
}

/**
 * Get the active lease for a property
 * @param {string} propertyId - Property ID
 * @returns {Promise<Object|null>} Active lease or null
 */
export async function getActiveLease(propertyId) {
  try {
    const { data, error } = await supabase
      .from('leases')
      .select('*')
      .eq('property_id', propertyId)
      .eq('status', 'active')
      .maybeSingle();

    if (error) throw error;
    return data || null;
  } catch (error) {
    console.error('Error fetching active lease:', error);
    throw error;
  }
}

/**
 * Get a single lease by ID
 * @param {string} leaseId - Lease ID
 * @returns {Promise<Object>} Lease object
 */
export async function getLease(leaseId) {
  try {
    const { data, error } = await supabase
      .from('leases')
      .select('*')
      .eq('id', leaseId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching lease:', error);
    throw error;
  }
}

/**
 * Create a new lease
 * @param {Object} leaseData - Lease data
 * @returns {Promise<Object>} Created lease
 */
export async function createLease(leaseData) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const lease = {
      property_id: leaseData.propertyId,
      tenant_name: leaseData.tenantName,
      tenant_email: leaseData.tenantEmail || null,
      tenant_phone: leaseData.tenantPhone || null,
      start_date: leaseData.startDate,
      end_date: leaseData.endDate || null,
      rental_amount: leaseData.rentalAmount,
      rental_currency: leaseData.rentalCurrency || 'PLN',
      payment_day: leaseData.paymentDay || null,
      deposit_amount: leaseData.depositAmount || null,
      status: leaseData.status || 'active',
      notes: leaseData.notes || null,
      contract_attachment_path: leaseData.contractAttachmentPath || null,
    };

    const { data, error } = await supabase
      .from('leases')
      .insert([lease])
      .select()
      .single();

    if (error) throw error;

    // If creating an active lease, update property status to 'rented'
    if (data.status === 'active') {
      await updatePropertyRentalStatus(leaseData.propertyId, 'rented');
    }

    return data;
  } catch (error) {
    console.error('Error creating lease:', error);
    throw error;
  }
}

/**
 * Update an existing lease
 * @param {string} leaseId - Lease ID
 * @param {Object} updates - Lease updates
 * @returns {Promise<Object>} Updated lease
 */
export async function updateLease(leaseId, updates) {
  try {
    // Get current lease to check property_id and old status
    const { data: oldLease, error: fetchError } = await supabase
      .from('leases')
      .select('property_id, status')
      .eq('id', leaseId)
      .single();

    if (fetchError) throw fetchError;

    const updateData = {
      tenant_name: updates.tenantName,
      tenant_email: updates.tenantEmail || null,
      tenant_phone: updates.tenantPhone || null,
      start_date: updates.startDate,
      end_date: updates.endDate || null,
      rental_amount: updates.rentalAmount,
      rental_currency: updates.rentalCurrency || 'PLN',
      payment_day: updates.paymentDay || null,
      deposit_amount: updates.depositAmount || null,
      status: updates.status || oldLease.status,
      termination_reason: updates.terminationReason || null,
      termination_date: updates.terminationDate || null,
      notes: updates.notes || null,
      contract_attachment_path: updates.contractAttachmentPath || null,
    };

    const { data, error } = await supabase
      .from('leases')
      .update(updateData)
      .eq('id', leaseId)
      .select()
      .single();

    if (error) throw error;

    // Update property status if lease status changed
    const newStatus = updateData.status;
    if (newStatus !== oldLease.status) {
      if (newStatus === 'active') {
        await updatePropertyRentalStatus(oldLease.property_id, 'rented');
      } else if (newStatus === 'terminated' || newStatus === 'ended') {
        await updatePropertyRentalStatus(oldLease.property_id, 'available');
      }
    }

    return data;
  } catch (error) {
    console.error('Error updating lease:', error);
    throw error;
  }
}

/**
 * Terminate a lease (change status to terminated)
 * @param {string} leaseId - Lease ID
 * @param {string} reason - Termination reason
 * @param {string} terminationDate - Termination date (YYYY-MM-DD)
 * @returns {Promise<Object>} Updated lease
 */
export async function terminateLease(leaseId, reason, terminationDate = null) {
  try {
    // First get the lease to know the property_id
    const { data: lease, error: fetchError } = await supabase
      .from('leases')
      .select('property_id')
      .eq('id', leaseId)
      .single();

    if (fetchError) throw fetchError;

    const { data, error } = await supabase
      .from('leases')
      .update({
        status: 'terminated',
        termination_reason: reason,
        termination_date: terminationDate || new Date().toISOString().split('T')[0],
      })
      .eq('id', leaseId)
      .select()
      .single();

    if (error) throw error;

    // Update property status to 'available'
    await updatePropertyRentalStatus(lease.property_id, 'available');

    return data;
  } catch (error) {
    console.error('Error terminating lease:', error);
    throw error;
  }
}

/**
 * Archive a lease (change status to archived)
 * @param {string} leaseId - Lease ID
 * @returns {Promise<Object>} Updated lease
 */
export async function archiveLease(leaseId) {
  try {
    const { data, error } = await supabase
      .from('leases')
      .update({
        status: 'archived',
      })
      .eq('id', leaseId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error archiving lease:', error);
    throw error;
  }
}

/**
 * Delete a lease (hard delete)
 * @param {string} leaseId - Lease ID
 * @returns {Promise<void>}
 */
export async function deleteLease(leaseId) {
  try {
    const { error } = await supabase
      .from('leases')
      .delete()
      .eq('id', leaseId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting lease:', error);
    throw error;
  }
}

/**
 * Get all leases for the current user (across all properties)
 * @returns {Promise<Array>} Array of leases with property info
 */
export async function getAllUserLeases() {
  try {
    // Ensure session is valid before making request
    const isValid = await ensureValidSession();
    if (!isValid) {
      console.warn('[getAllUserLeases] Invalid session, returning empty array');
      return [];
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('leases')
      .select(`
        *,
        properties!inner (
          id,
          street,
          city,
          building_number,
          apartment_number,
          postal_code
        )
      `)
      .eq('properties.user_id', user.id)
      .order('start_date', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching all user leases:', error);
    throw error;
  }
}
